import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
// tslint:disable-next-line: max-line-length
import { CartActions, StateWithMultiCart } from '@spartacus/cart/base/core';
import { ActiveCartFacade } from '@spartacus/cart/base/root';
import { BaseSiteService, ClientTokenService, TranslationService, UserIdService } from '@spartacus/core';
import { Observable, combineLatest, throwError } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { ASM_ENABLED_LOCAL_STORAGE_KEY } from 'src/app/spartacus/features/asm/asm-constant';
import { environment } from 'src/environments/environment';
import { ShimanoCountryService } from '../country/shimano-country.service';
import { DownloadOptions, ShimanoFileDownloadService } from '../file/shimano-file-download.service';
import { StorageService } from '../storage/storage.service';

export interface DownloadCsvPayload {
  cartId: string;
  userId: string;
}

export interface SaveCartPayload {
  name: string;
  description: string;
}

export interface SavedCartsListPayload {
  currentPage: number;
  fields: string;
  pageSize?: number;
  savedCartsOnly: true;
  sort: string;
  sortType: string;
}

export interface AddQuickOrderPayload {
  productCode: string;
  quantity: number;
}

export interface UdpateMemoPayload {
  entryNumber: number;
  memo: String;
}

@Injectable({
  providedIn: 'root',
})
export class CartExtendService {
  baseSite$: Observable<string> = this.baseService.getActive();
  userCurrent$: Observable<string> = this.auth.getUserId();
  activeCart$: Observable<string> = this.cartService.getActiveCartId();
  occUrl = this.meta.getTag('name=occ-backend-base-url');
  url = `${this.occUrl.content}${environment.prefix}${this.baseSiteId}`;
  headers = new HttpHeaders().set('Content-Type', 'application/json');
  asmEnabled = this.storageService.getLocalStorageItem(ASM_ENABLED_LOCAL_STORAGE_KEY);
  headersFileUpload = new HttpHeaders();
  sortLabels: Observable<{
    byDateSavedDesc: string;
    byDateSavedAsc: string;
    byCreatorNameDesc: string;
    byCreatorNameAsc: string;
  }>;
  fileToUpload: File;

  constructor(
    protected http: HttpClient,
    protected baseService: BaseSiteService,
    protected auth: UserIdService,
    protected clientToken: ClientTokenService,
    protected cartService: ActiveCartFacade,
    protected translation: TranslationService,
    protected store: Store<StateWithMultiCart>,
    protected shimanoCountryService: ShimanoCountryService,
    protected shimanoFileDownloadService: ShimanoFileDownloadService,
    private storageService: StorageService,
    private meta: Meta
  ) {}

  get baseSiteId(): string {
    let baseSiteId: string;
    this.baseSite$.pipe(take(1)).subscribe((baseSiteData) => (baseSiteId = baseSiteData));
    return baseSiteId;
  }

  get userCurrent(): string {
    let userCurrent: string;
    this.userCurrent$.pipe(take(1)).subscribe((userId) => (userCurrent = userId));
    return userCurrent;
  }

  get tokenId(): string {
    let tokenId: string;
    this.clientToken
      .getClientToken()
      .pipe(take(1))
      .subscribe((tokenIdData) => (tokenId = tokenIdData.access_token));
    return tokenId;
  }

  get activeCartId() {
    let activeCartId: string;
    this.activeCart$.pipe(take(1)).subscribe((activeCart) => (activeCartId = activeCart));
    return activeCartId;
  }

  get country(): string {
    let country: string;
    this.shimanoCountryService
      .getActive()
      .pipe(take(1))
      .subscribe((data) => (country = data));
    return country;
  }

  getSelectedCountry() {
    return this.country;
  }

  getCartPageID(): Observable<any> {
    return this.http
      .get(`../../../public/features/CART_PAGE_ID.json`)
      .pipe(catchError((error: any) => throwError(error)));
  }

  getMiniCartDetails(refreshCart = false): Observable<any> {
    const refreshCartFalse = refreshCart ? '' : `?refreshCart=false`;
    return this.http
      .get(`${this.url}/users/${this.userCurrent}/carts/current/minicart${refreshCartFalse}`, {
        headers: this.headers,
      })
      .pipe(catchError((error: any) => throwError(error)));
  }

  downloadCartCsv(payload: DownloadCsvPayload): Observable<any> {
    const options: DownloadOptions = {
      responseType: 'arraybuffer',
    };

    return this.shimanoFileDownloadService.downloadCsv(
      'GET',
      `${this.url}/users/${payload.userId}/carts/${payload.cartId}/export`,
      options,
      'CART'
    );
  }

  saveCart(payload: SaveCartPayload, cartId: string): Observable<any> {
    return this.http
      .patch(
        // tslint:disable-next-line: max-line-length
        `${this.url}/users/${this.userCurrent}/carts/${cartId}/savedCart`,
        payload
      )
      .pipe(catchError((error: any) => throwError(error)));
  }

  getActiveCartId(): Observable<string> {
    return this.cartService.getActiveCartId();
  }

  getCustomerSavedCarts(payload: SavedCartsListPayload): Observable<any> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');

    const params = new HttpParams()
      .set('currentPage', payload.currentPage as any)
      .set('fields', payload.fields)
      .set('pageSize', payload.pageSize as any)
      .set('savedCartsOnly', payload.savedCartsOnly as any)
      .set('sort', payload.sort)
      .set('sortType', payload.sortType);
    return this.http
      .get(`${this.url}/users/${this.userCurrent}/savedCarts`, { headers, params })
      .pipe(catchError((error: any) => throwError(error)));
  }

  restoreSavedCart(cartId: string, refreshCart = false): Observable<any> {
    const refreshCartFalse = refreshCart ? '' : `?refreshCart=false`;
    return this.http
      .patch(`${this.url}/users/${this.userCurrent}/carts/${cartId}/restoresavedcart${refreshCartFalse}`, {
        headers: this.headers,
      })
      .pipe(catchError((error: any) => throwError(error)));
  }

  // Get saved cart Details
  getSavedCartDetails(cartId: string, refreshCart = false): Observable<any> {
    const refreshCartFalse = refreshCart ? '' : `?refreshCart=false`;
    return this.http
      .get(`${this.url}/users/${this.userCurrent}/carts/${cartId}/savedcart${refreshCartFalse}`, {
        headers: this.headers,
      })
      .pipe(catchError((error: any) => throwError(error)));
  }

  // Update saved cart Details
  updatSavedCartDetails(payload: SaveCartPayload, cartId: string): Observable<any> {
    return this.http
      .patch(`${this.url}/users/${this.userCurrent}/carts/${cartId}/save`, { payload, headers: this.headers })
      .pipe(catchError((error: any) => throwError(error)));
  }

  loadCurrentToActiveCart(cartId: string) {
    this.store.dispatch(
      new CartActions.LoadCart({
        userId: this.userCurrent,
        cartId,
        extraData: {
          active: true,
        },
      })
    );
  }

  getCurrentCart(): Observable<any> {
    return this.http
      .get(`${this.url}/users/${this.userCurrent}/carts/current?fields=FULL`, { headers: this.headers })
      .pipe(catchError((error: any) => throwError(error)));
  }

  getCurrentCartByID(cartId: string): Observable<any> {
    return this.http
      .get(`${this.url}/users/${this.userCurrent}/carts/${cartId}?fields=FULL`, { headers: this.headers })
      .pipe(catchError((error: any) => throwError(error)));
  }

  getImportCartErrorList(code: any): Observable<any> {
    return (
      this.http
        // tslint:disable-next-line: max-line-length
        .get(`${this.url}/users/${this.userCurrent}/carts/${code}/savedCartImportErrors?fields=DEFAULT`, {
          headers: this.headers,
        })
        .pipe(catchError((error: any) => throwError(error)))
    );
  }

  deleteSavedCart(cartId: string): Observable<any> {
    return this.http
      .patch(`${this.url}/users/${this.userCurrent}/carts/${cartId}/flagForDeletion`, { headers: this.headers })
      .pipe(catchError((error: any) => throwError(error)));
  }

  getSortLabels(): Observable<{
    byDateSavedDesc: string;
    byDateSavedAsc: string;
    byCreatorNameDesc: string;
    byCreatorNameAsc: string;
  }> {
    return combineLatest([
      this.translation.translate('cart.sortLabels.dateSavedDesc'),
      this.translation.translate('cart.sortLabels.dateSavedAsc'),
      this.translation.translate('cart.sortLabels.creatorNameDesc'),
      this.translation.translate('cart.sortLabels.creatorNameAsc'),
    ]).pipe(
      map(([textByDateSavedDesc, textByDateSavedAsc, textCreatorNameDesc, textByCreatorNameAsc]) => {
        return {
          byDateSavedDesc: textByDateSavedDesc,
          byDateSavedAsc: textByDateSavedAsc,
          byCreatorNameDesc: textCreatorNameDesc,
          byCreatorNameAsc: textByCreatorNameAsc,
        };
      })
    );
  }

  saveAddressToCart(addressId: string, cartId: string) {
    return this.http
      .put(`${this.url}/users/${this.userCurrent}/carts/${cartId}/addresses/delivery?fields=FULL`, null, {
        params: {
          addressId,
        },
        headers: this.headers,
      })
      .pipe(catchError((error: any) => throwError(error)));
  }

  postCsvImportFile(file: any) {
    const formData = new FormData();
    formData.append('csvFile', file, file.name);
    return this.http
      .post(`${this.url}/users/${this.userCurrent}/carts?action=handleSavedCartImport`, formData, {
        headers: this.headersFileUpload,
      })
      .pipe(catchError((error: any) => throwError(error)));
  }
  getSavedCart(code: string, refreshCart = false): Observable<any> {
    const refreshCartFalse = refreshCart ? '' : `?refreshCart=false`;
    return this.http
      .get(`${this.url}/users/${this.userCurrent}/carts/${code}/savedcart${refreshCartFalse}`, {
        headers: this.headers,
      })
      .pipe(catchError((error: any) => throwError(error)));
  }

  reorderService(code: string, erpOrderType?: string): Observable<any> {
    const params = new HttpParams().set('erpOrderType', erpOrderType);
    return this.http
      .post(`${this.url}/users/${this.userCurrent}/${code}/reorder`, null, {
        params,
        headers: this.headers,
      })
      .pipe(catchError((error: any) => throwError(error)));
  }

  addToQuickOrder(payload: AddQuickOrderPayload, activeCartId: string): Observable<any> {
    const entries = {
      cartEntries: payload,
    };
    return this.http
      .post(`${this.url}/users/${this.userCurrent}/carts/${activeCartId}/addQuickOrder`, entries, {
        headers: this.headers,
      })
      .pipe(catchError((error: any) => throwError(error)));
  }

  getCartProcessStatus(code: string, failOnError = true, refreshCart = false): Observable<any> {
    const refreshCartFalse = refreshCart ? '' : `&refreshCart=false`;
    return this.http
      .get(`${this.url}/users/${this.userCurrent}/carts/${code}/status?failOnError=${failOnError}${refreshCartFalse}`, {
        headers: this.headers,
      })
      .pipe(catchError((error: any) => throwError(error)));
  }

  // Performs cart validation. Validates the given cart.
  checkCartValidation(cartId: string): Observable<any> {
    const asmQuery = this.asmEnabled ? `?asmUser=${this.userCurrent}` : '';
    return this.http
      .post(`${this.url}/users/${this.userCurrent}/carts/${cartId}/validation`, { headers: this.headers })
      .pipe(catchError((error: any) => throwError(error)));
  }

  createEmptyCart(): Observable<any> {
    const httpOptions = {
      headers: this.headers,
      observe: 'body' as 'response',
    };

    return this.http
      .post(`${this.url}/users/${this.userCurrent}/carts/`, null, httpOptions)
      .pipe(catchError((error: any) => throwError(error)));
  }

  udpateMemo(payload: UdpateMemoPayload, entryNumber: number, memo: string): Observable<any> {
    return this.http
      .patch(`${this.url}/users/${this.userCurrent}/carts/${this.activeCartId}/entries/${entryNumber}/memo`, payload, {
        params: {
          memo,
        },
      })
      .pipe(catchError((error: any) => throwError(error)));
  }

  deleteCart(cartId: string): Observable<any> {
    return this.http
      .delete(`${this.url}/users/${this.userCurrent}/carts/${cartId}`, {
        headers: this.headers,
      })
      .pipe(catchError((error: any) => throwError(error)));
  }
}
